import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCheck, faChevronRight, faPlus } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import type { ProductLandingContent } from '../data/productLandingContent'
import { Footer, HeaderMiddle, Navbar, TopBar } from './SiteChrome'
import type { StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { getProductPrice, getProductPrimaryImage, getProductPrimaryImageUrl, toExternalImage } from '../lib/dropify'
import { track, trackSelectItem, trackViewItem, whatsAppCtaProps } from '../lib/analytics'
import { AdNewsProofSection } from './HomeNewsSection'

const WA_NUMBER = '6285704011028'

export function buildProductWhatsAppLink(
  productName: string,
  content: ProductLandingContent,
  fields: { quantity: string; detail: string; city: string },
) {
  const clean = (value: string) => value.trim().slice(0, 120) || '___'
  const message = [
    `Halo Dropify, saya mau cek ${productName}.`,
    `Kebutuhan: ${clean(fields.quantity)}${content.unit ? ` ${content.unit}` : ' (satuan mohon dikonfirmasi)'}.`,
    `${content.messageDetail}: ${clean(fields.detail)}.`,
    `Kota tujuan: ${clean(fields.city)}.`,
    content.messageRequest,
  ].join(' ')
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

export function ProductLandingPage({
  config,
  product,
  content,
}: {
  config: StorefrontConfig | null
  product: StorefrontProduct
  content: ProductLandingContent
}) {
  const [quantity, setQuantity] = useState('')
  const [detail, setDetail] = useState('')
  const [city, setCity] = useState('')
  const [selectedImage, setSelectedImage] = useState(0)
  const link = buildProductWhatsAppLink(product.name, content, { quantity, detail, city })
  const primaryUrl = getProductPrimaryImageUrl(product)
  const images = primaryUrl
    ? Array.from(new Set([primaryUrl, ...product.photos.map((item) => item.url), ...product.variants.flatMap((variant) => variant.photos.map((item) => item.url))]))
    : []
  const photo = images.length > 0
    ? toExternalImage(images[selectedImage] ?? images[0], { width: 1000, height: 1000, fitIn: true, format: 'webp', quality: 82 })
    : getProductPrimaryImage(product, { width: 1000, height: 1000, fitIn: true, format: 'webp', quality: 82 })
  const variants = product.variants.map((variant) => variant.name?.trim() || variant.sku).filter(Boolean)

  useEffect(() => {
    setSelectedImage(0)
  }, [product.id])

  useEffect(() => {
    trackViewItem({
      item_id: product.id,
      item_name: product.name,
      item_category: content.category,
      price: getProductPrice(product),
    })
  }, [product.id, product.name, product, content.category])

  const leadCta = (placement: string) => {
    return whatsAppCtaProps('product_page', {
      cta_placement: placement,
      lp_category: content.category,
      lead_item_name: product.name,
      items: [{ item_id: product.id, item_name: product.name, item_category: content.category }],
    })
  }

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <main>
        <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50/70 pb-12 pt-6 md:pb-16 md:pt-8">
          <div className="mx-auto max-w-7xl px-4">
            <nav aria-label="Breadcrumb" className="mb-7 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
              <a href="/" className="hover:text-blue-700">Beranda</a>
              <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
              <a href={content.parentHref} className="hover:text-blue-700">{content.parentLabel}</a>
              <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
              <span className="font-semibold text-slate-700">{product.name}</span>
            </nav>

            <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-12">
              <div className="lg:order-2 lg:pt-3">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">{content.eyebrow}</p>
                <h1 className="mt-3 text-3xl font-black leading-tight tracking-tight text-slate-950 md:text-4xl lg:text-[2.75rem]">{product.name}</h1>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-700 md:text-lg">{content.summary}</p>

                <div className="mt-6 rounded-2xl border border-blue-200 bg-white p-5 shadow-sm">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-700">Ketentuan pembelian</p>
                  <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-900 md:text-base">{content.buyingNote}</p>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">Harga dan stok dikonfirmasi admin saat permintaan quotation.</p>
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    {...leadCta('product_hero')}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-green-700 px-6 py-3 text-center text-sm font-bold text-white shadow-sm transition-colors hover:bg-green-800"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
                    Cek stok dan minta quotation
                  </a>
                  <a href="#permintaan-penawaran" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 transition-colors hover:border-blue-400 hover:text-blue-700">
                    Isi detail kebutuhan <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>

              <div className="lg:order-1">
                <div className="flex aspect-square items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:p-8">
                  <img src={photo} alt={selectedImage ? `${product.name}, tampilan ${selectedImage + 1}` : product.name} width="1000" height="1000" fetchPriority="high" decoding="async" className="h-full w-full object-contain" />
                </div>
                {images.length > 1 && (
                  <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                    {images.map((url, index) => (
                      <button key={url} type="button" onClick={() => setSelectedImage(index)} aria-label={`Lihat gambar ${index + 1} ${product.name}`} aria-pressed={selectedImage === index} className={`h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 bg-white p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedImage === index ? 'border-blue-600' : 'border-slate-200 hover:border-blue-300'}`}>
                        <img src={toExternalImage(url, { width: 180, height: 180, fitIn: true, format: 'webp', quality: 70 })} alt="" width="88" height="88" loading="lazy" decoding="async" className="h-full w-full object-contain" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {variants.length > 1 && (
          <section className="bg-white py-10 md:py-12" aria-labelledby="varian-produk">
            <div className="mx-auto max-w-7xl px-4">
              <h2 id="varian-produk" className="text-2xl font-black tracking-tight text-slate-950">Pilihan model atau varian</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">Sebutkan pilihan yang dicari saat meminta quotation. Stok tiap varian diperiksa admin.</p>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {variants.map((name, index) => <li key={`${name}-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">{name}</li>)}
              </ul>
            </div>
          </section>
        )}

        <section className="border-y border-slate-200 bg-white py-10 md:py-14" aria-labelledby="fakta-produk">
          <div className="mx-auto max-w-7xl px-4">
            <h2 id="fakta-produk" className="text-2xl font-black tracking-tight text-slate-950">Detail yang perlu diketahui</h2>
            <dl className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {content.facts.map((fact) => (
                <div key={fact.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-5">
                  <dt className="text-xs font-semibold text-slate-500">{fact.label}</dt>
                  <dd className="mt-2 text-base font-bold leading-snug text-slate-900">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="py-12 md:py-16" aria-label="Panduan memilih produk">
          <div className="mx-auto grid max-w-7xl gap-5 px-4 md:grid-cols-2">
            <div className="rounded-3xl bg-blue-50 p-6 md:p-8">
              <h2 className="text-xl font-black text-slate-950 md:text-2xl">{content.fitTitle}</h2>
              <ul className="mt-5 space-y-4">
                {content.fit.map((item) => <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-700 md:text-base"><FontAwesomeIcon icon={faCheck} className="mt-1 h-3.5 w-3.5 shrink-0 text-blue-700" /><span>{item}</span></li>)}
              </ul>
            </div>
            <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 md:p-8">
              <h2 className="text-xl font-black text-slate-950 md:text-2xl">{content.checkTitle}</h2>
              <ul className="mt-5 space-y-4">
                {content.checks.map((item) => <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-700 md:text-base"><span className="mt-0.5 shrink-0 font-black text-amber-700">•</span><span>{item}</span></li>)}
              </ul>
            </div>
          </div>
        </section>

        <section id="permintaan-penawaran" className="scroll-mt-6 bg-slate-950 py-12 text-white md:py-16" aria-labelledby="judul-penawaran">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center lg:gap-16">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-green-300">Permintaan quotation</p>
              <h2 id="judul-penawaran" className="mt-3 text-2xl font-black leading-tight md:text-3xl">Kirim kebutuhan {product.name} ke admin</h2>
              <p className="mt-4 text-sm leading-relaxed text-slate-300 md:text-base">Isi jumlah, detail pilihan, dan kota tujuan. Pesan WhatsApp akan disiapkan agar admin bisa langsung memeriksa stok serta penawaran yang sesuai.</p>
              <ol className="mt-7 space-y-3 text-sm text-slate-200">
                <li><span className="mr-3 font-bold text-green-300">01</span>Anda kirim kebutuhan lewat WhatsApp.</li>
                <li><span className="mr-3 font-bold text-green-300">02</span>Admin cek stok, pilihan, dan syarat pembelian.</li>
                <li><span className="mr-3 font-bold text-green-300">03</span>Admin menyiapkan quotation dan informasi pengiriman.</li>
              </ol>
            </div>
            <div className="rounded-3xl bg-white p-5 text-slate-900 shadow-xl md:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-semibold">{content.unit ? `Jumlah (${content.unit})` : 'Jumlah perkiraan'}
                  <input inputMode="numeric" type="number" min="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder={content.quantityPlaceholder} className="mt-2 h-12 w-full rounded-xl border border-slate-300 px-4 text-base font-normal outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </label>
                <label className="block text-sm font-semibold">Kota tujuan
                  <input type="text" autoComplete="address-level2" value={city} onChange={(event) => setCity(event.target.value)} placeholder="Contoh: Surabaya" className="mt-2 h-12 w-full rounded-xl border border-slate-300 px-4 text-base font-normal outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </label>
                <label className="block text-sm font-semibold sm:col-span-2">{content.detailLabel}
                  <input type="text" value={detail} onChange={(event) => setDetail(event.target.value)} placeholder={content.detailPlaceholder} className="mt-2 h-12 w-full rounded-xl border border-slate-300 px-4 text-base font-normal outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
                </label>
              </div>
              <a href={link} target="_blank" rel="noopener noreferrer" {...leadCta('product_quote_builder')} className="mt-6 inline-flex min-h-13 w-full items-center justify-center gap-2 rounded-xl bg-green-700 px-5 py-3 text-center text-sm font-bold text-white transition-colors hover:bg-green-800">
                <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" /> Kirim permintaan via WhatsApp
              </a>
              <p className="mt-3 text-center text-xs text-slate-500">Data yang belum diisi dapat dilengkapi langsung di WhatsApp.</p>
            </div>
          </div>
        </section>

        <AdNewsProofSection />

        <section className="py-12 md:py-16" aria-labelledby="faq-produk">
          <div className="mx-auto grid max-w-7xl gap-9 px-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
            <div>
              <h2 id="faq-produk" className="text-2xl font-black tracking-tight text-slate-950">Pertanyaan sebelum order</h2>
              <div className="mt-5 divide-y divide-slate-200 border-y border-slate-200">
                {content.faq.map((item) => (
                  <details key={item.q} className="group py-4" onToggle={(event) => { if (event.currentTarget.open) track('faq_open', { faq_question: item.q, faq_location: 'product_page' }) }}>
                    <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-4 text-sm font-bold leading-relaxed text-slate-900 marker:hidden"><span>{item.q}</span><FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5 shrink-0 text-blue-700 transition-transform group-open:rotate-45" /></summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                  </details>
                ))}
              </div>
              <p className="mt-5 text-sm leading-relaxed text-slate-600">
                Ingin memeriksa pesanan pertama sebelum transfer?{' '}
                <a href="/jaminan-ekspedisi" className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">Lihat alur verifikasi melalui ekspedisi pilihan</a>.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight text-slate-950">Bandingkan pilihan</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">Lihat model lain jika kebutuhan ukuran, kemasan, atau pemakaian Anda berbeda.</p>
              <div className="mt-5 space-y-3">
                {content.alternatives.map((item) => <a key={item.href} href={item.href} onClick={() => { if (item.itemId) trackSelectItem({ item_id: item.itemId, item_name: item.label, item_category: item.itemCategory, price: item.price }, 'related_products') }} className="group flex items-center justify-between gap-4 rounded-2xl border border-slate-200 p-5 transition-colors hover:border-blue-400 hover:bg-blue-50"><span><span className="block text-sm font-bold text-slate-900 group-hover:text-blue-700">{item.label}</span><span className="mt-1 block text-xs leading-relaxed text-slate-600">{item.description}</span></span><FontAwesomeIcon icon={faArrowRight} className="h-4 w-4 shrink-0 text-blue-700" /></a>)}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer config={config} />
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-8px_28px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
        <a href={link} target="_blank" rel="noopener noreferrer" {...leadCta('product_mobile_sticky')} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-center text-sm font-bold text-white">
          <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" /> Cek stok dan minta quotation
        </a>
      </div>
    </div>
  )
}
